import { css } from "@emotion/react";
import styled from "@emotion/styled";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Info } from "@livechat/design-system-icons";
import {
  Button,
  Icon as DSIcon,
  DesignToken,
  Heading,
  IPickerListItem,
  Loader,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import imageSrc from "assets/img/welcome/import-data.png";
import { ALL_TIME_KEY, ticketImportTimeRangeOptions } from "components/ImportTicketsDateRangeSelect";
import { Icon, IconsEnum } from "components/icon";
import Flex from "components/layout/Flex";
import { SubscriptionPhase } from "constants/subscription-phase";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getSubscriptionPhase } from "helpers/subscription";
import { useTicketsToImport } from "hooks/useTicketsToImport";
import { LiveChatDataImportType } from "routes/settings/integrations/constants";
import { IHasTicketsResponse } from "services/api/integrations/integrations-api";
import { getItem as getSessionStoreItem, saveItem as saveSessionStoreItem } from "services/session-storage";
import { getNonViewersCount } from "store/entities/agents";
import { getBilling } from "store/entities/billing";
import {
  IntegrationsActions,
  selectIsFetchingLiveChatSelectiveImportDiff,
  selectIsManagedByShopify,
  selectIsShopifyTrial,
  selectLiveChatSelectiveImportDiff,
} from "store/entities/integrations";
import { selectManagedByPartner } from "store/entities/license";
import { selectCurrentSubscription, selectSeatsCount } from "store/entities/subscriptions";
import { selectIsDarkMode } from "store/features/app";
import { WelcomeTutorialViewActions } from "store/views/welcome-tutorial";
import { withVar } from "themes/helpers";

import { WelcomeTutorialRoutes } from "../helpers";

import { ImportOptionCard } from "./ImportOptionCard";
import { ImportTicketsOptionCard } from "./ImportTicketsOptionCard";
import TitleOnboardingStep from "./TitleOnboardingStep";

const SELECTED_IMPORTS_ITEM = "selected-imports";
const SELECTED_TICKET_IMPORT_DATE_RANGE_ITEM = "selected-ticket-import-date-range";

export const ImportDataStep: FC = () => {
  const dispatch = useDispatch();

  const billing = useSelector(getBilling);
  const subscription = useSelector(selectCurrentSubscription);
  const seatsCount = useSelector(selectSeatsCount);
  const nonViewersCount = useSelector(getNonViewersCount);
  const importDiff = useSelector(selectLiveChatSelectiveImportDiff);
  const isFetchingImportDiff = useSelector(selectIsFetchingLiveChatSelectiveImportDiff);
  const isHandledByShopify = useSelector(selectIsManagedByShopify);
  const isShopifyTrial = useSelector(selectIsShopifyTrial);
  const isManagedByPartner = useSelector(selectManagedByPartner);
  const isDarkModeTheme = useSelector(selectIsDarkMode);

  const [selectedImports, setSelectedImports] = useState<LiveChatDataImportType[]>(
    getSessionStoreItem(SELECTED_IMPORTS_ITEM) || []
  );
  const [ticketsImportDateRange, setTicketsImportDateRange] = useState<IPickerListItem | null>(
    ticketImportTimeRangeOptions.find(
      option => option.name === getSessionStoreItem(SELECTED_TICKET_IMPORT_DATE_RANGE_ITEM)
    ) || null
  );
  const subscriptionPhase = getSubscriptionPhase(subscription, billing);
  const agentsToImport = importDiff?.agents?.create || 0;
  const seatsToAdd = agentsToImport - (seatsCount - nonViewersCount);
  const shouldShowDisclaimer =
    (subscriptionPhase === SubscriptionPhase.Paid ||
      subscriptionPhase === SubscriptionPhase.PaidNoCC ||
      (isHandledByShopify && !isShopifyTrial) ||
      isManagedByPartner) &&
    seatsToAdd > 0;

  const { ticketsToImport, isLoading: checkingTicketsToImport } = useTicketsToImport(
    ({ hasTickets }: IHasTicketsResponse) => {
      if (!getSessionStoreItem(SELECTED_IMPORTS_ITEM)) {
        setSelectedImports(hasTickets ? allImportOptions : selectiveImportOptions);
      }
    }
  );

  const allImportOptions = _.values(LiveChatDataImportType);
  const selectiveImportOptions = allImportOptions.filter(importType => importType !== LiveChatDataImportType.Tickets);
  const noImportSelected = selectedImports.length === 0;
  const isTicketsImportSelected = selectedImports.includes(LiveChatDataImportType.Tickets);
  const loading = !ticketsToImport || checkingTicketsToImport || isFetchingImportDiff;
  const noTicketsImportDateRangeSelected = isTicketsImportSelected && !ticketsImportDateRange;
  const disableNextButton = loading || noTicketsImportDateRangeSelected;

  const isImportOptionSelected = (importOption: LiveChatDataImportType): boolean =>
    selectedImports.includes(importOption);

  const updateSelectedImports = (updatedImports: LiveChatDataImportType[]): void => {
    setSelectedImports(updatedImports);
    saveSessionStoreItem(SELECTED_IMPORTS_ITEM, updatedImports);
  };

  const updateTicketsImportDateRange = (updatedDateRange: IPickerListItem): void => {
    setTicketsImportDateRange(updatedDateRange);
    saveSessionStoreItem(SELECTED_TICKET_IMPORT_DATE_RANGE_ITEM, updatedDateRange.name);
  };

  const handleSelectImportClick = (importOption: LiveChatDataImportType): void => {
    if (importOption === LiveChatDataImportType.Tickets && !isImportOptionSelected(importOption)) {
      updateSelectedImports(allImportOptions);
    } else {
      updateSelectedImports(
        isImportOptionSelected(importOption)
          ? selectedImports.filter(selectedImport => selectedImport !== importOption)
          : [...selectedImports, importOption]
      );
    }
  };

  const finishTutorial = (): void => {
    dispatch(WelcomeTutorialViewActions.finish());
    trackEngagement("LC integration: Welcome screens finished", EventPlace.Onboarding);
  };

  const startImport = (): void => {
    trackEngagement("LC integration: Import data", EventPlace.Onboarding, {
      importedData: isTicketsImportSelected ? "full" : selectedImports.sort().join("+"),
      firstTime: true,
      eventPlace: "welcomeScreen",
      timeRange: ticketsImportDateRange?.key,
    });

    if (isTicketsImportSelected && ticketsImportDateRange) {
      dispatch(
        IntegrationsActions.startLiveChatImport({
          ...(ticketsImportDateRange.key === ALL_TIME_KEY ? {} : { importDataFrom: ticketsImportDateRange.key }),
        })
      );
    } else {
      dispatch(
        IntegrationsActions.startLiveChatSelectiveImport({
          types: selectedImports as Exclude<LiveChatDataImportType, LiveChatDataImportType.Tickets>[],
        })
      );
    }
  };

  const handleNextButtonClick = (): void => {
    finishTutorial();

    if (!noImportSelected) {
      startImport();
    }
  };

  useEffect(() => {
    if (!importDiff) {
      dispatch(IntegrationsActions.fetchLiveChatImportDiff());
    }

    trackEngagement("LC integration: Welcome screens", EventPlace.Onboarding, {
      step: 4,
    });
  }, [isTicketsImportSelected]);

  const NextButton = (
    <Button size="large" kind="primary" disabled={disableNextButton} onClick={handleNextButtonClick}>
      {noImportSelected ? "Continue" : "Import data"}
    </Button>
  );

  return (
    <TitleOnboardingStep
      currentStep={4}
      title={
        <Flex alignItems="center" gap="8px" marginBottom="8px">
          <Heading size="lg" customColor={withVar(DesignToken.ContentBasicPrimary)}>
            Import data from
          </Heading>
          <Icon
            name={isDarkModeTheme ? IconsEnum.LivechatFullLogoWhiteText : IconsEnum.LivechatFullLogoBlackText}
            width="106px"
            height="24px"
            css={css`
              padding-top: 6px;
            `}
          />
        </Flex>
      }
      subtitle="Choose the data you want to import."
      content={
        loading ? (
          <Flex alignContent="center" justifyContent="center" minHeight="300px">
            <Loader size="medium" />
          </Flex>
        ) : (
          <>
            <Flex gap={withVar(SpacingToken.Spacing2)} marginTop="24px" column>
              {ticketsToImport.hasTickets && (
                <ImportTicketsOptionCard
                  selected={isImportOptionSelected(LiveChatDataImportType.Tickets)}
                  onOptionClick={() => handleSelectImportClick(LiveChatDataImportType.Tickets)}
                  selectedDateRange={ticketsImportDateRange}
                  onDateRangeSelect={updateTicketsImportDateRange}
                  showUpdateSubscriptionDisclaimer={shouldShowDisclaimer}
                />
              )}
              <ImportOptionCard
                type={LiveChatDataImportType.AgentsAndGroups}
                title="Agents and groups"
                onClick={handleSelectImportClick}
                selected={isImportOptionSelected(LiveChatDataImportType.AgentsAndGroups)}
                ticketsImportSelected={isTicketsImportSelected}
                addons={
                  shouldShowDisclaimer && (
                    <Flex flexGrow={1} justifyContent="flex-end">
                      <Tooltip
                        triggerRenderer={
                          <DSIcon source={Info} customColor={withVar(DesignToken.ContentBasicPrimary)} />
                        }
                        placement="right"
                        kind="invert"
                        css={css`
                          max-width: 220px;
                        `}
                      >
                        Imported LiveChat users will keep their roles in HelpDesk and{" "}
                        <b>be billed per the chosen HelpDesk plan</b>.
                      </Tooltip>
                    </Flex>
                  )
                }
              />
              <ImportOptionCard
                type={LiveChatDataImportType.CannedResponses}
                title="Canned responses"
                onClick={handleSelectImportClick}
                selected={isImportOptionSelected(LiveChatDataImportType.CannedResponses)}
                ticketsImportSelected={isTicketsImportSelected}
                addons={
                  <Flex flexGrow={1} justifyContent="flex-end">
                    <Tooltip
                      triggerRenderer={<DSIcon source={Info} customColor={withVar(DesignToken.ContentBasicPrimary)} />}
                      placement="right"
                      kind="invert"
                      css={css`
                        max-width: 220px;
                      `}
                    >
                      Only shared canned responses will be imported.
                    </Tooltip>
                  </Flex>
                }
              />
              <ImportOptionCard
                type={LiveChatDataImportType.Tags}
                title="Tags"
                onClick={handleSelectImportClick}
                selected={isImportOptionSelected(LiveChatDataImportType.Tags)}
                ticketsImportSelected={isTicketsImportSelected}
              />
            </Flex>
            <ImportInfo size="sm">
              Importing may take time. Follow progress in HelpDesk settings.
              <br />
              All imported agents will get an agent/admin role in HelpDesk. This change is irreversible and will be done
              for the entire account. You can also import the data later in the settings.
            </ImportInfo>
          </>
        )
      }
      imageSource={imageSrc}
      nextButton={
        noTicketsImportDateRangeSelected ? (
          <Tooltip triggerRenderer={NextButton} placement="top" kind="invert">
            Select the ticket import period first.
          </Tooltip>
        ) : (
          NextButton
        )
      }
      backButtonRouteLink={WelcomeTutorialRoutes.LC_MANAGEABLE_TICKETS}
    />
  );
};

const ImportInfo = styled(Text)`
  margin-top: ${withVar(SpacingToken.Spacing2)};
  margin-bottom: 26px;
  color: ${withVar(DesignToken.ContentBasicDisabled)};
`;
